import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

interface LocationState {
  email?: string;
  passwordResetRequired?: boolean;
}

const ForgotPassword = () => {
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const location = useLocation();
  const { forgotPassword } = useAuth();
  const state = location.state as LocationState | null;
  const [email, setEmail] = useState(state?.email || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const passwordResetRequired = state?.passwordResetRequired || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await forgotPassword(email);
      if (result.success) {
        portalNavigate('/reset-password', { state: { email } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader
          title="Forgot Password"
          subtitle="Enter your email to reset your password"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {passwordResetRequired && (
            <div className="rounded-md bg-amber-50 p-4">
              <div className="text-sm text-amber-700">
                Your password has been reset by an administrator. Please reset
                your password to continue.
              </div>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="text"
              required
              className={actionFormInputClassName}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`${actionAuthSubmitButtonClassName} disabled:opacity-50`}
            >
              {isLoading ? 'Processing...' : 'Reset Password'}
            </button>
          </div>

          <div className="text-center text-sm">
            <Link
              to={portalPath('/login')}
              className={`font-medium ${actionLinkClassName}`}
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
